import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import { NSInput } from 'bricks';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { NSInfoBanner } from 'components/NSInfoBanner/NSInfoBanner';
import useToast from 'hooks/useToast';
import { useAuth0 } from '@auth0/auth0-react';
import { STATIC_URLS } from 'services/urls';
import { PRO_FORMA_TABS } from 'views/ProFormaTable/constants';
import { useDeactivatedUserSupport } from 'views/public/SignUp/hooks';
import { SocialSignUpButtons } from 'views/public/SignUp/components';
import { SocialSignUpConnectionToLabel } from 'views/public/SignUp/constants';

export interface ISignUpSectionProps {
    setCurrentTab: (tabName: string) => void;
    setDisplayManualSignUp: (display: boolean) => void;
    signUpManager: ISignUpManager;
}

const SignUpSection = ({
    setCurrentTab, setDisplayManualSignUp, signUpManager,
}: ISignUpSectionProps) => {
    const {
        email,
        userOrgLookupData,
        socialSignUpEnabled,
        socialSignUpConnectionType,
        handleEmailChange,
        handleSocialSignUp,
        resetSignUpState,
    } = signUpManager;

    const { mutate: sendDeactivatedUserSupport } = useDeactivatedUserSupport();
    const { showSuccess, showError } = useToast();
    const { loginWithRedirect } = useAuth0();

    const isUserDisabled = userOrgLookupData?.user?.isDisabled === true;

    const hasActiveUserAndOrg = userOrgLookupData?.user
        && !isUserDisabled
        && userOrgLookupData?.org
        && !email.errorMsg;

    const hasNoUserButExistingOrg = !userOrgLookupData?.user && userOrgLookupData?.org && !email.errorMsg;

    const getButtonText = () => {
        if (hasActiveUserAndOrg) {
            return 'Start using Northspyre Deal';
        }
        if (socialSignUpConnectionType) {
            return `Continue with my ${SocialSignUpConnectionToLabel[socialSignUpConnectionType]} account`;
        }
        return 'Continue';
    };

    const isButtonDisabled = isUserDisabled || !userOrgLookupData || !email.value || !!email.errorMsg;

    const handleContactSupport = () => {
        const userEmail = userOrgLookupData?.user?.email;

        if (!userEmail) {
            console.error('User email not available');
            return;
        }

        sendDeactivatedUserSupport(
            {
                user_email: userEmail,
            },
            {
                onSuccess: () => {
                    showSuccess('A customer support representative will be in touch with you via email soon.');
                },
                onError: error => {
                    showError('Failed to send support request. Please try again or email concierge@northspyre.com directly.');
                },
            },
        );
    };

    const deactivatedAccountMessage = isUserDisabled ? (
        <span>
            This is a deactivated account. An admin will need to restore your access.
            {' '}
            <button
                type="button"
                onClick={handleContactSupport}
                className="text-primary bg-transparent border-0 p-0 font-inherit"
            >
                Contact support
            </button>
            .
        </span>
    ) : '';

    const handleButtonClick = () => {
        if (hasActiveUserAndOrg) {
            loginWithRedirect({
                authorizationParams: {
                    login_hint: email.value,
                },
            });
        } else {
            setCurrentTab(PRO_FORMA_TABS.DEVELOPMENT.name);
            setDisplayManualSignUp(true);
        }
    };

    return (
        <div>
            <div className="text-center mb-1">
                <p className="text-white mb-0">Create an account to see full insights.</p>
                <small className="mt-0">
                    By creating an account, you agree to our
                    {' '}
                    <a
                        href={STATIC_URLS.TERMS_AND_CONDITIONS}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Terms and Conditions
                    </a>
                    {' '}
                    and
                    {' '}
                    <a
                        href={STATIC_URLS.PRIVACY_POLICY}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy.
                    </a>
                </small>
            </div>
            <NSInput
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={email.value}
                onChange={handleEmailChange}
                className="mx-2"
                autoComplete="email"
                invalid={!!email.errorMsg}
                showErrorMsg={!!email.errorMsg}
                errorMsg={email.errorMsg}
                errorMsgClassName="mx-2"
            />
            {isUserDisabled && (
                <NSInfoBanner
                    title={deactivatedAccountMessage as any}
                    className="mt-2 mx-2 NSCard__left-border--danger"
                    boldTitle={false}
                    showIcon={false}
                />
            )}
            {hasNoUserButExistingOrg && (
                <NSInfoBanner
                    title="Your organization already uses Northspyre."
                    className="mt-2 mx-2 NSCard__left-border--info"
                    customIcon={faInfoCircle}
                    iconSize="sm"
                    boldTitle={false}
                />
            )}
            {hasActiveUserAndOrg && (
                <NSInfoBanner
                    title="You already use Northspyre. We've added access to Northspyre Deal."
                    className="mt-2 mx-2 NSCard__left-border--info"
                    customIcon={faInfoCircle}
                    iconSize="sm"
                    boldTitle={false}
                />
            )}
            <div className="px-2 mt-2">
                <NSButtonBase
                    color="primary"
                    callback={handleButtonClick}
                    disabled={isButtonDisabled}
                    className="w-100 heap-deal-signup-step-3-create-account-email"
                    dataTestId="sidebarCreateAccountButton"
                >
                    {getButtonText()}
                </NSButtonBase>
                { socialSignUpConnectionType && (
                    <div className="w-100 d-flex justify-content-center">
                        <NSButtonBase
                            callback={resetSignUpState || (() => undefined)}
                            className="DirectSignUp__LinkButton"
                            disabled={!resetSignUpState}
                            dataTestId="resetSignUpButton"
                        >
                            Change sign up method
                        </NSButtonBase>
                    </div>
                ) }
            </div>
            <div className="DirectSignUp__Divider">
                <hr className="DirectSignUp__DividerLine" />
                <span className="DirectSignUp__DividerText">OR</span>
                <hr className="DirectSignUp__DividerLine" />
            </div>
            <SocialSignUpButtons
                handleSocialSignUp={handleSocialSignUp}
                enabled={socialSignUpEnabled}
            />
        </div>
    );
};

export default SignUpSection;
